(() => {
  const initialised = new WeakSet();

  const initialiseCoverflow = (section) => {
    if (!section || initialised.has(section)) return;

    const viewport = section.querySelector('[data-jc-coverflow-viewport]');
    const track = section.querySelector('.jc-ingredient-story__grid');
    const cards = Array.from(section.querySelectorAll('.jc-ingredient-story__card'));
    const previous = section.querySelector('[data-jc-coverflow-prev]');
    const next = section.querySelector('[data-jc-coverflow-next]');

    if (!viewport || !track || cards.length < 2) return;

    // Keep the template's first card as the deterministic initial slide.
    // The previous middle-card start caused Sweet potato to appear first and
    // made the next-arrow sequence feel like 2 → 3 → 1 after refresh.
    let activeIndex = 0;
    let dragStartX = 0;
    let dragStartY = 0;
    let dragging = false;
    let moved = false;

    const normaliseIndex = (index) => (index + cards.length) % cards.length;

    const update = (animate = true) => {
      const activeCard = cards[activeIndex];
      const cardWidth = activeCard.offsetWidth;
      const cardCenter = activeCard.offsetLeft + cardWidth / 2;
      const viewportCenter = viewport.clientWidth / 2;
      const padding = Math.max(0, viewportCenter - cardWidth / 2);

      track.style.transitionDuration = animate ? '' : '0ms';
      track.style.paddingInline = `${padding}px`;
      track.style.transform = `translate3d(${viewportCenter - cardCenter}px, 0, 0)`;

      cards.forEach((card, index) => {
        const distance = Math.min(
          Math.abs(index - activeIndex),
          cards.length - Math.abs(index - activeIndex)
        );
        card.classList.toggle('is-coverflow-active', index === activeIndex);
        card.classList.toggle('is-coverflow-neighbor', distance === 1);
        card.setAttribute('aria-hidden', index === activeIndex ? 'false' : 'true');
      });
    };

    const moveTo = (index) => {
      activeIndex = normaliseIndex(index);
      update(true);
    };

    previous?.addEventListener('click', () => moveTo(activeIndex - 1));
    next?.addEventListener('click', () => moveTo(activeIndex + 1));

    viewport.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        moveTo(activeIndex - 1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        moveTo(activeIndex + 1);
      }
    });

    const startDrag = (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      dragging = true;
      moved = false;
      dragStartX = event.clientX;
      dragStartY = event.clientY;
      section.classList.add('is-coverflow-dragging');
      viewport.setPointerCapture?.(event.pointerId);
    };

    const moveDrag = (event) => {
      if (!dragging) return;
      const deltaX = event.clientX - dragStartX;
      const deltaY = event.clientY - dragStartY;
      if (Math.abs(deltaX) > 8 && Math.abs(deltaX) > Math.abs(deltaY)) {
        moved = true;
        event.preventDefault();
      }
    };

    const endDrag = (event) => {
      if (!dragging) return;
      dragging = false;
      section.classList.remove('is-coverflow-dragging');
      const deltaX = event.clientX - dragStartX;
      if (Math.abs(deltaX) > 36) moveTo(activeIndex + (deltaX < 0 ? 1 : -1));
      if (event.pointerId !== undefined && viewport.hasPointerCapture?.(event.pointerId)) {
        viewport.releasePointerCapture(event.pointerId);
      }
    };

    viewport.addEventListener('pointerdown', startDrag);
    viewport.addEventListener('pointermove', moveDrag);
    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);
    viewport.addEventListener('click', (event) => {
      if (!moved) return;
      event.preventDefault();
      event.stopPropagation();
      moved = false;
    }, true);

    const resizeObserver = new ResizeObserver(() => update(false));
    resizeObserver.observe(viewport);
    window.requestAnimationFrame(() => update(false));
    initialised.add(section);
  };

  const initialiseAll = (root = document) => {
    root.querySelectorAll?.('[data-jc-ingredient-coverflow]').forEach(initialiseCoverflow);
    if (root.matches?.('[data-jc-ingredient-coverflow]')) initialiseCoverflow(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAll());
  } else {
    initialiseAll();
  }

  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
})();
